import type {
	DsaiIngestionUnsupportedReason,
	IngestionFilePagesRes,
	IngestionLibraryDocumentRes,
} from '@dealstack/shared/lib/generated';
import type { LibraryQuery } from '@dealstack/frontend/app/services/dealstackAi/ingestion.types';
import { filePagesView, formatLibraryDate } from '../ingestion.utils';
import type { LibraryPageSelection, LibraryRowView } from './useLibraryPicker.types';

const UNSUPPORTED_REASON_KEYS: Record<DsaiIngestionUnsupportedReason, string> = {
	unsupported_format: 'dealstack_ai.ingest.library.unsupported.unsupported_format',
};

/** The list query; a blank search and "All classifications" are left out. */
export const toLibraryQuery = ({
	search,
	classification,
	page,
	pageSize,
}: {
	search: string;
	classification: string | null;
	page: number;
	pageSize: number;
}): LibraryQuery => ({
	page,
	pageSize,
	...(search.trim() === '' ? {} : { search: search.trim() }),
	...(classification === null ? {} : { classification }),
});

export const toLibraryRow = (
	document: IngestionLibraryDocumentRes,
	selected: ReadonlySet<string>,
	countedPages: IngestionFilePagesRes | null
): LibraryRowView => ({
	id: document.id,
	name: document.name,
	classifications: document.classifications,
	relatedPeople: document.relatedPeople,
	lastUpdatedLabel: formatLibraryDate(document.lastUpdated),
	selected: selected.has(document.id),
	selectable: document.selectable,
	unsupportedKey:
		document.unsupportedReason === null
			? null
			: UNSUPPORTED_REASON_KEYS[document.unsupportedReason],
	analysedIn: document.analysedIn,
	pages: filePagesView(countedPages ?? document.pages),
});

/** The header checkbox over the page's selectable rows. */
export const pageSelectionOf = (rows: LibraryRowView[]): LibraryPageSelection => {
	const selectable = rows.filter((row) => row.selectable);
	const selectedOnPage = selectable.filter((row) => row.selected).length;
	if (selectedOnPage === 0) {
		return 'none';
	}
	return selectedOnPage === selectable.length ? 'all' : 'some';
};
